import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { buildBoostAdName, buildBoostAdSetName } from '@fluenceur/types';
import { User } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface PlacementNamingPreviewProps {
  campaignName: string;
  productHandle: string | null;
  productTitle: string | null;
  creatorUsername: string;
}

/**
 * What Fluenceur will create inside the brand's campaign, named with the exact
 * convention the launch applies (the builders are shared with the backend). The
 * point is that nothing Fluenceur creates is anonymous in the brand's reports.
 */
export function PlacementNamingPreview({
  campaignName,
  productHandle,
  productTitle,
  creatorUsername,
}: PlacementNamingPreviewProps) {
  const { t } = useTranslation();
  const namingInput = { productHandle, productTitle, creatorUsername };

  return (
    <Box border="1px solid" borderColor="border.muted" bg="bg.subtle" rounded="lg" px={3} py={3}>
      <VStack align="stretch" gap={2.5}>
        <Text fontSize="xs" fontWeight="semibold">
          {t('mentions.boost.setup.placement.namingTitle', { campaign: campaignName })}
        </Text>

        <NamingRow
          label={t('mentions.boost.setup.placement.namingAdSetLabel')}
          value={buildBoostAdSetName(namingInput)}
        />
        <NamingRow
          label={t('mentions.boost.setup.placement.namingAdLabel')}
          value={buildBoostAdName(namingInput)}
        />

        <HStack gap={1.5} align="flex-start">
          <Icon size="xs" color="fg.muted" mt={0.5}>
            <User />
          </Icon>
          <Text fontSize="xs" color="fg.muted">
            {t('mentions.boost.setup.placement.attribution')}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
}

function NamingRow({ label, value }: { label: string; value: string }) {
  return (
    <VStack align="stretch" gap={1}>
      <Text fontSize="2xs" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
        {label}
      </Text>
      <Text fontFamily="mono" fontSize="xs" wordBreak="break-all">
        {value}
      </Text>
    </VStack>
  );
}
